// ============================================================================
// EMAIL DARK MODE CONVERTER - FINÁLNÍ VERZE
// Gmail mění JEN background-color a color (text), nic jiného!
// ============================================================================

interface RGB {
  r: number;
  g: number;
  b: number;
  a: number;
}

interface HSL {
  h: number;
  s: number;
  l: number;
  a: number;
}

// ============================================================================
// PARSOVÁNÍ BAREV
// ============================================================================

function parseColor(color: string): RGB | null {
  // Hex formát (#ffffff nebo #fff)
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    return {
      r: parseInt(hex.substr(0, 2), 16),
      g: parseInt(hex.substr(2, 2), 16),
      b: parseInt(hex.substr(4, 2), 16),
      a: 1,
    };
  }

  // RGB/RGBA formát
  const match = color.match(
    /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/
  );
  if (match) {
    return {
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
      a: match[4] ? parseFloat(match[4]) : 1,
    };
  }

  return null;
}

// ============================================================================
// HSL KONVERZE
// ============================================================================

function rgbToHsl(rgb: RGB): HSL {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h, s, l, a: rgb.a };
}

function hslToRgb(hsl: HSL): RGB {
  const { h, s, l, a } = hsl;
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a,
  };
}

// ============================================================================
// DARK MODE KONVERZE
// ============================================================================

/**
 * Gmail - TEXT s ohledem na pozadí
 */
function gmailTextColor(textColor: string, bgColor?: string): string {
  const rgb = parseColor(textColor);
  if (!rgb) return textColor;

  const hsl = rgbToHsl(rgb);

  // Bílá/černá → inverze
  if (hsl.l < 0.05) {
    if (textColor.startsWith("#")) {
      return "#FFFFFF";
    }
    return "rgba(255, 255, 255, 1)";
  }

  if (hsl.l > 0.95) {
    if (textColor.startsWith("#")) {
      return "#202124";
    }
    return "rgba(32, 33, 36, 1)";
  }

  // Pokud máme pozadí, spočítej kontrast
  if (bgColor) {
    const bgRgb = parseColor(bgColor);
    if (bgRgb) {
      const bgHsl = rgbToHsl(bgRgb);

      // Tmavé pozadí → zesvětlit text
      if (bgHsl.l < 0.5) {
        hsl.l = Math.max(0.9, hsl.l * 1.5);
        hsl.s = hsl.s * 0.9;
      }
      // Světlé pozadí → ztmavit text
      else {
        hsl.l = Math.min(0.35, hsl.l * 0.5);
        hsl.s = Math.min(1, hsl.s * 1.1);
      }
    }
  } else {
    // BEZ pozadí - NOVÝ ALGORITMUS podle dat!
    if (hsl.s < 0.4) {
      // NÍZKÁ SATURACE (šedivé) → zrcadlení kolem 0.6
      hsl.l = 1.2 - hsl.l;
      hsl.s = hsl.s * 0.6; // Desaturuj trochu
    } else {
      // VYSOKÁ SATURACE (barevné)
      if (hsl.l < 0.52) {
        // Tmavé sytě barvy → výrazně ztmavit
        hsl.l = hsl.l * 0.5;
        hsl.s = Math.min(1, hsl.s * 1.1);
      } else if (hsl.l < 0.6) {
        // Středně světlé → zesvětlit
        hsl.l = hsl.l * 1.2;
        hsl.s = hsl.s * 0.98;
      } else {
        // Světlé → lehce ztmavit
        hsl.l = hsl.l * 0.7;
        hsl.s = hsl.s * 0.95;
      }
    }
  }

  const result = hslToRgb(hsl);

  if (textColor.startsWith("#")) {
    return `#${[result.r, result.g, result.b]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")}`;
  }

  return `rgba(${result.r}, ${result.g}, ${result.b}, ${result.a})`;
}

/**
 * Gmail - POZADÍ: Možná stejná logika?
 */
function gmailBackgroundColor(color: string): string {
  const rgb = parseColor(color);
  if (!rgb) return color;

  const hsl = rgbToHsl(rgb);

  // Černá → bílá
  if (hsl.l < 0.05) {
    if (color.startsWith("#")) return "#FFFFFF";
    return "rgba(255, 255, 255, 1)";
  }

  // Bílá → téměř černá
  if (hsl.l > 0.95) {
    if (color.startsWith("#")) return "#202124";
    return "rgba(32, 33, 36, 1)";
  }

  // SPECIÁLNÍ OŠETŘENÍ pro sytě barevné středně světlé (l 0.45-0.65)
  if (hsl.l >= 0.45 && hsl.l <= 0.65 && hsl.s > 0.5) {
    // Červená (h: 0-0.05) → minimální změna
    if (hsl.h <= 0.05 || hsl.h >= 0.97) {
      hsl.l = hsl.l * 0.93;
      hsl.s = hsl.s * 0.97;
    }
    // Žlutá/zelená (h: 0.08-0.45) → výrazně ztmavit
    else if (hsl.h >= 0.08 && hsl.h <= 0.45) {
      hsl.l = hsl.l * 0.35;
      hsl.s = hsl.s * 0.85;
    }
    // Fialová (h: 0.7-0.85) → lehce ztmavit
    else if (hsl.h >= 0.7 && hsl.h <= 0.85) {
      hsl.l = hsl.l * 0.87;
      hsl.s = hsl.s * 0.95;
    }
    // Ostatní → původní logika
    else {
      hsl.l = 0.15 + (1 - hsl.l) * 0.5;
      hsl.s = hsl.s * 0.7;
    }
  }
  // ALGORITMUS pro ostatní
  else if (hsl.l < 0.5) {
    hsl.l = 0.9 - hsl.l * 0.4;
    hsl.s = hsl.s * 0.85;
  } else {
    hsl.l = 0.15 + (1 - hsl.l) * 0.5;
    hsl.s = hsl.s * 0.7;
  }

  const result = hslToRgb(hsl);

  if (color.startsWith("#")) {
    return `#${[result.r, result.g, result.b]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")}`;
  }

  return `rgba(${result.r}, ${result.g}, ${result.b}, ${result.a})`;
}

/**
 * Aplikace na HTML
 */
function applyDarkMode(htmlString: string): string {
  let result = htmlString;

  const colorPattern = `#[0-9a-fA-F]{3,6}\\b|rgba?\\(\\s*\\d+\\s*,\\s*\\d+\\s*,\\s*\\d+\\s*(?:,\\s*[\\d.]+)?\\s*\\)`;

  // 1. background-color
  result = result.replace(
    new RegExp(`background-color\\s*:\\s*(${colorPattern})`, "gi"),
    (match, color) => {
      const convertedColor = gmailBackgroundColor(color.trim());
      return match.replace(color, convertedColor);
    }
  );

  // 2. background
  result = result.replace(
    new RegExp(`background\\s*:\\s*(${colorPattern})(?=\\s|;|})`, "gi"),
    (match, color) => {
      if (/linear-gradient|radial-gradient/.test(match)) {
        return match;
      }
      const convertedColor = gmailBackgroundColor(color.trim());
      return match.replace(color, convertedColor);
    }
  );

  // 3. border-color (všechny varianty)
  result = result.replace(
    new RegExp(
      `border(-top|-right|-bottom|-left)?-color\\s*:\\s*(${colorPattern})`,
      "gi"
    ),
    (match, side, color) => {
      const convertedColor = gmailBackgroundColor(color.trim());
      return match.replace(color, convertedColor);
    }
  );

  // 4. border shorthand (např. "border: 2px solid #fff")
  result = result.replace(
    new RegExp(`border(-top|-right|-bottom|-left)?\\s*:\\s*([^;]+)`, "gi"),
    (match, side, borderValue) => {
      const colorMatch = borderValue.match(new RegExp(colorPattern, "i"));
      if (colorMatch) {
        const oldColor = colorMatch[0];
        const newColor = gmailBackgroundColor(oldColor.trim());
        return match.replace(oldColor, newColor);
      }
      return match;
    }
  );

  // Do DOM (jen pro COLOR!)
  const tempContainer = document.createElement("div");
  tempContainer.style.display = "none";
  tempContainer.innerHTML = result;
  document.body.appendChild(tempContainer);

  const styledElements = tempContainer.querySelectorAll("[style]");

  styledElements.forEach((element) => {
    const htmlElement = element as HTMLElement;

    // Zjisti gradient/url
    let hasGradientBg = false;
    let current: HTMLElement | null = htmlElement;
    while (current) {
      const styleAttr = current.getAttribute("style") || "";
      if (/gradient|url\(/.test(styleAttr)) {
        hasGradientBg = true;
        break;
      }
      current = current.parentElement as HTMLElement;
    }

    let computedBg = hasGradientBg
      ? undefined
      : window.getComputedStyle(htmlElement).backgroundColor;

    let el = htmlElement.parentElement;
    while (!computedBg || parseColor(computedBg)?.a === 0) {
      // Transparentní → jdi nahoru
      if (el && el.parentElement) {
        el = el.parentElement;
        const parentStyle = window.getComputedStyle(el);
        if (parentStyle.backgroundImage !== "none") {
          break; // Má obrázek/gradient → zastav
        }
        if (parseColor(parentStyle.backgroundColor)?.a === 0) {
          if (parentStyle.backgroundImage !== "none") {
            break; // Má obrázek/gradient → zastav
          }
          continue; // Stále průhledné
        }
        computedBg = parentStyle.backgroundColor;
      } else {
        break;
      }
    }

    // Změň JEN COLOR (border už je hotový!)
    if (htmlElement.style.color) {
      const oldColor = htmlElement.style.color;
      const newColor = gmailTextColor(oldColor, computedBg);
      htmlElement.style.color = newColor;
    }
  });

  result = tempContainer.innerHTML;
  document.body.removeChild(tempContainer);

  return result;
}

// ============================================================================
// EXPORT
// ============================================================================

export { applyDarkMode, gmailBackgroundColor, gmailTextColor };

export type { RGB, HSL };
